import React, { useState, useEffect, useCallback } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import api from "@/lib/api";
import { toast } from "sonner";
import { FileText, Download, Loader2, Receipt } from "lucide-react";

export default function InvoicesModal({ open, onOpenChange }) {
  const [invoices, setInvoices] = useState([]);
  const [loading, setLoading] = useState(false);
  const [downloading, setDownloading] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/invoices");
      setInvoices(data);
    } catch {
      toast.error("Impossibile caricare le fatture");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (open) load();
  }, [open, load]);

  const download = async (inv) => {
    setDownloading(inv.id);
    try {
      const res = await api.get(`/payments/${inv.id}/invoice`, { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = `fattura_${inv.number}.pdf`;
      a.click();
      URL.revokeObjectURL(url);
    } catch {
      toast.error("Errore durante il download della fattura");
    } finally {
      setDownloading(null);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-lg border-border/60 bg-card" data-testid="invoices-modal">
        <DialogHeader>
          <div className="mx-auto h-11 w-11 rounded-2xl bg-brand-blue/10 flex items-center justify-center mb-1">
            <Receipt className="h-5 w-5 text-brand-blue" />
          </div>
          <DialogTitle className="text-center font-heading text-xl font-medium">Le tue fatture</DialogTitle>
          <DialogDescription className="text-center text-sm text-muted-foreground">
            Scarica le ricevute PDF dei tuoi pagamenti.
          </DialogDescription>
        </DialogHeader>

        <div className="max-h-[420px] overflow-auto -mx-1 px-1">
          {loading ? (
            <div className="flex items-center justify-center py-10 text-muted-foreground">
              <Loader2 className="h-5 w-5 animate-spin" />
            </div>
          ) : invoices.length === 0 ? (
            <div data-testid="invoices-empty" className="flex flex-col items-center gap-2 py-10 text-center">
              <FileText className="h-8 w-8 text-muted-foreground/50" />
              <p className="text-sm text-muted-foreground">Nessuna fattura disponibile.</p>
              <p className="text-xs text-muted-foreground/70">Le fatture appariranno qui dopo un pagamento.</p>
            </div>
          ) : (
            <div className="space-y-2">
              {invoices.map((inv) => (
                <div
                  key={inv.id}
                  data-testid={`invoice-row-${inv.id}`}
                  className="flex items-center justify-between rounded-xl border border-border bg-background/50 px-4 py-3 hover:border-brand-blue/40 transition-colors"
                >
                  <div className="min-w-0">
                    <div className="flex items-center gap-2">
                      <p className="text-sm font-medium truncate">{inv.number}</p>
                      <span className="text-xs font-semibold tabular-nums text-brand-pink">{inv.amount.toLocaleString("it-IT", { minimumFractionDigits: 2 })} {inv.currency}</span>
                    </div>
                    <p className="text-xs text-muted-foreground truncate">{inv.label} · {inv.date}</p>
                  </div>
                  <Button
                    size="sm"
                    variant="outline"
                    data-testid={`invoice-download-${inv.id}`}
                    onClick={() => download(inv)}
                    disabled={downloading === inv.id}
                    className="rounded-full gap-1.5 shrink-0"
                  >
                    {downloading === inv.id ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Download className="h-3.5 w-3.5" />} PDF
                  </Button>
                </div>
              ))}
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
